import {
  boolean,
  index,
  int,
  json,
  mysqlEnum,
  mysqlTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/mysql-core";

/** Google Calendar OAuth connection for the shared sales calendar. */
export const salesCalendarConnections = mysqlTable(
  "sales_calendar_connections",
  {
    id: int("id").autoincrement().primaryKey(),
    provider: varchar("provider", { length: 32 }).notNull().default("google"),
    googleCalendarId: varchar("googleCalendarId", { length: 320 }).notNull(),
    accountEmail: varchar("accountEmail", { length: 320 }),
    lifecycle: mysqlEnum("lifecycle", [
      "disconnected",
      "connected_disabled",
      "enabled",
      "paused",
      "attention_required",
    ])
      .notNull()
      .default("disconnected"),
    refreshTokenCiphertext: text("refreshTokenCiphertext"),
    refreshTokenIv: varchar("refreshTokenIv", { length: 64 }),
    refreshTokenAuthTag: varchar("refreshTokenAuthTag", { length: 64 }),
    grantedScopesJson: json("grantedScopesJson").$type<string[] | null>(),
    syncToken: varchar("syncToken", { length: 512 }),
    lastPollAttemptedAt: timestamp("lastPollAttemptedAt"),
    lastPollSucceededAt: timestamp("lastPollSucceededAt"),
    lastSafeErrorCode: varchar("lastSafeErrorCode", { length: 64 }),
    pollLeaseToken: varchar("pollLeaseToken", { length: 64 }),
    pollLeaseExpiresAt: timestamp("pollLeaseExpiresAt"),
    /** 1 while not disconnected; NULL when disconnected so MySQL unique permits historical rows. */
    activeSlot: int("activeSlot"),
    connectedByUserId: int("connectedByUserId"),
    connectedAt: timestamp("connectedAt"),
    enabledByUserId: int("enabledByUserId"),
    enabledAt: timestamp("enabledAt"),
    pausedByUserId: int("pausedByUserId"),
    pausedAt: timestamp("pausedAt"),
    disconnectedByUserId: int("disconnectedByUserId"),
    disconnectedAt: timestamp("disconnectedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  (t) => ({
    activeProviderUq: uniqueIndex("sales_cal_conn_active_provider_uq").on(
      t.provider,
      t.activeSlot,
    ),
    lifecycleIdx: index("sales_cal_conn_lifecycle_idx").on(
      t.lifecycle,
      t.updatedAt,
    ),
  }),
);

export const salesCalendarOauthStates = mysqlTable(
  "sales_calendar_oauth_states",
  {
    id: int("id").autoincrement().primaryKey(),
    stateHash: varchar("stateHash", { length: 128 }).notNull(),
    initiatingAdminUserId: int("initiatingAdminUserId").notNull(),
    expiresAt: timestamp("expiresAt").notNull(),
    consumedAt: timestamp("consumedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  (t) => ({
    stateHashUq: uniqueIndex("sales_cal_oauth_state_hash_uq").on(t.stateHash),
    adminCreatedIdx: index("sales_cal_oauth_admin_created_idx").on(
      t.initiatingAdminUserId,
      t.createdAt,
    ),
  }),
);

export const salesAppointments = mysqlTable(
  "sales_appointments",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId"),
    proposalId: int("proposalId"),
    title: varchar("title", { length: 500 }).notNull(),
    agendaNotes: text("agendaNotes"),
    startsAt: timestamp("startsAt").notNull(),
    endsAt: timestamp("endsAt").notNull(),
    /** Owner (person in charge). NULL = unmatched Google appointment (0295). */
    salespersonUserId: int("salespersonUserId"),
    salespersonNameSnapshot: varchar("salespersonNameSnapshot", { length: 255 }),
    /** Sync owner decision (0295): owner user id (null = unmatched) and how it was found. */
    ownerUserId: int("ownerUserId"),
    ownerSource: varchar("ownerSource", { length: 32 }),
    /** Raw Google creator / organizer emails from the last sync (0295). */
    googleCreatorEmail: varchar("googleCreatorEmail", { length: 320 }),
    googleOrganizerEmail: varchar("googleOrganizerEmail", { length: 320 }),
    googleEventId: varchar("googleEventId", { length: 256 }),
    googleCalendarId: varchar("googleCalendarId", { length: 320 }),
    googleEtag: varchar("googleEtag", { length: 128 }),
    googleHtmlLink: varchar("googleHtmlLink", { length: 1024 }),
    googleUpdatedAt: timestamp("googleUpdatedAt"),
    origin: mysqlEnum("origin", ["portal", "google"]).notNull().default("portal"),
    syncStatus: mysqlEnum("syncStatus", [
      "pending",
      "synced",
      "error",
      "local_only",
      "needs_customer_link",
    ])
      .notNull()
      .default("pending"),
    lastSyncedAt: timestamp("lastSyncedAt"),
    lastSyncError: varchar("lastSyncError", { length: 255 }),
    needsCustomerLink: boolean("needsCustomerLink").notNull().default(false),
    /** One-off "link a customer" email nudge (0285). */
    customerLinkNudgeSentAt: timestamp("customerLinkNudgeSentAt"),
    createdByUserId: int("createdByUserId"),
    updatedByUserId: int("updatedByUserId"),
    deletedAt: timestamp("deletedAt"),
    deletedByUserId: int("deletedByUserId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  (t) => ({
    googleEventUq: uniqueIndex("sales_appointments_google_event_uq").on(
      t.googleCalendarId,
      t.googleEventId,
    ),
    customerStartsIdx: index("sales_appointments_customer_starts_idx").on(
      t.customerId,
      t.startsAt,
    ),
    salespersonStartsIdx: index("sales_appointments_salesperson_starts_idx").on(
      t.salespersonUserId,
      t.startsAt,
    ),
    startsIdx: index("sales_appointments_starts_idx").on(t.startsAt),
    proposalIdx: index("sales_appointments_proposal_idx").on(t.proposalId),
    deletedIdx: index("sales_appointments_deleted_idx").on(t.deletedAt),
  }),
);

export const salesAppointmentAuditEvents = mysqlTable(
  "sales_appointment_audit_events",
  {
    id: int("id").autoincrement().primaryKey(),
    appointmentId: int("appointmentId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }),
    source: mysqlEnum("source", ["portal", "google_sync", "system"])
      .notNull()
      .default("portal"),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  (t) => ({
    apptCreatedIdx: index("sales_appt_audit_appt_created_idx").on(
      t.appointmentId,
      t.createdAt,
    ),
    eventCreatedIdx: index("sales_appt_audit_event_created_idx").on(
      t.eventType,
      t.createdAt,
    ),
  }),
);

export type SalesCalendarConnection = typeof salesCalendarConnections.$inferSelect;
export type InsertSalesCalendarConnection =
  typeof salesCalendarConnections.$inferInsert;
export type SalesCalendarOauthState = typeof salesCalendarOauthStates.$inferSelect;
export type SalesAppointment = typeof salesAppointments.$inferSelect;
export type InsertSalesAppointment = typeof salesAppointments.$inferInsert;
export type SalesAppointmentAuditEvent =
  typeof salesAppointmentAuditEvents.$inferSelect;
